import { ExclamationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import parse from 'html-react-parser';
import { useCallback, useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from '@/components/core/Popover';
import { ValidationStateIndicator } from '@/components/core/validationState/ValidationState';
import { usePopoverManager } from '@/components/datamodel/hooks/usePopoverManager';
import { useValidationsStore } from '@/components/datamodel/hooks/useValidationsStore';
import { useValidationTranslator } from '@/components/datamodel/hooks/useValidationTranslator';

type ValidationType = 'errors' | 'warnings';

interface SectionValidationPopoverProps {
    sectionAttributeNames: string[];
    type: ValidationType;
}

const SectionValidationPopover = ({ sectionAttributeNames, type }: SectionValidationPopoverProps) => {
    const popoverId = `section-${type}-${sectionAttributeNames.join('-')}`;
    const { isOpen, openPopover, closePopover, closeAllPopovers } = usePopoverManager(popoverId);

    const { translateValidationLabel } = useValidationTranslator();

    // Use the optimized selector to get validations for multiple attributes at once
    const validations = useValidationsStore(useShallow(state =>
        state.getValidationsForAttributes(sectionAttributeNames, type)
    ));

    // Memoize the translated validations to prevent re-parsing on every render
    const translatedValidations = useMemo(() => {
        return validations.map((validation) => {
            return (
                <p key={`${validation.attributeName}:${validation.name}`}
                    className="tw-line-clamp-2 tw-break-words tw-overflow-hidden tw-text-t-sm">
                    {parse(
                        translateValidationLabel(
                            validation.name,
                            validation.attributeName,
                        ) ?? '',
                    )}
                </p>);
        });
    }, [validations, translateValidationLabel]);

    const handleScroll = useCallback(() => {
        closeAllPopovers();
    }, [closeAllPopovers]);

    useEffect(() => {
        const scrollContainer = document.getElementById('item-editor-body');
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                scrollContainer.removeEventListener('scroll', handleScroll);
            };
        }
    }, [handleScroll]);

    // Early return if no validations to avoid unnecessary rendering
    if (!validations || validations.length === 0) {
        return null;
    }

    const isError = type === 'errors';
    const iconVariant = isError ? 'destructive' : 'warning';
    const badgeVariant = isError ? 'error' : 'warning';
    const IconComponent = isError ? XCircleIcon : ExclamationCircleIcon;

    return (
        <Popover open={isOpen} onOpenChange={(open) => open ? openPopover() : closePopover()}>
            <PopoverTrigger
                asChild
                onMouseEnter={openPopover}
                onClick={(e) => e.stopPropagation()}
            >
                <ValidationStateIndicator
                    icon={IconComponent}
                    iconVariant={iconVariant}
                    count={validations.length}
                    badgeVariant={badgeVariant}
                />
            </PopoverTrigger>
            <PopoverPortal>
                <PopoverContent
                    className="tw-w-full tw-max-w-[30vw] tw-max-h-[50vh] tw-p-6 tw-overflow-y-auto tw-whitespace-pre-wrap"
                    side="right"
                    sideOffset={-15}
                    onMouseEnter={openPopover}
                    onMouseLeave={closePopover}
                    onClick={(e) => e.stopPropagation()}
                    onScroll={handleScroll}
                >
                    <div className="tw-space-y-2">
                        {translatedValidations}
                    </div>
                </PopoverContent>
            </PopoverPortal>
        </Popover>
    );
};

export { SectionValidationPopover };
export type { ValidationType };
